/**
 * useEvalsConfigForm: the edits, validation and save for a team's
 * customer config row (TRA-1215), over
 * PUT /api/internal/teams/:id/evals-config, which creates the row when
 * the team has none.
 *
 * Two staff pages edit that one row. Evals config holds the nightly
 * scoring setup, and Eval PRs holds the deny list and the repository
 * layout. Each renders only its own fields, and a save sends the whole
 * row, so the fields a page does not show go back as they were loaded.
 * A save therefore also sends the row version the form was loaded from,
 * and the api refuses it (409) when the row has moved since. Without
 * that, a form opened before another tab or page saved would put that
 * save's old values back.
 *
 * The saved values are the query; the form holds the edits. A save writes
 * the API's answer into the query cache, so the form reads clean at once
 * and the other page opens on what was saved.
 */

import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import {
  getGetTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGetQueryKey,
  getGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGetQueryKey,
  useUpdateTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigPut,
} from '@/api/generated/internal/internal'
import type { BenchmarkAdapterItem, TeamEvalsConfigResponse } from '@/api/generated/model'
import {
  emptyForm,
  formFromConfig,
  isFormDirty,
  toBody,
  validateForm,
  type EvalsConfigForm,
} from '@/components/internal/team-config/evalsConfigForm'

export interface EvalsConfigFormState {
  form: EvalsConfigForm
  update: <K extends keyof EvalsConfigForm>(key: K, value: EvalsConfigForm[K]) => void
  edit: (next: (previous: EvalsConfigForm) => EvalsConfigForm) => void
  /** The team has no row yet, so a save creates it. */
  creates: boolean
  dirty: boolean
  pending: boolean
  canSubmit: boolean
  /** The form's first problem, or the API's answer to the last save. */
  error: string | null
  discard: () => void
  submit: (e: FormEvent) => void
}

export function useEvalsConfigForm(
  teamId: string,
  setup: TeamEvalsConfigResponse,
  adapters: readonly BenchmarkAdapterItem[],
): EvalsConfigFormState {
  const creates = setup.config === null
  // A team with no row starts blank; saving it creates the row.
  const saved = setup.config === null ? emptyForm() : formFromConfig(setup.config)
  const [form, setForm] = useState<EvalsConfigForm>(saved)
  // The version `form` was loaded from, kept with it rather than read off
  // the query: a refetch that brings another save must not vouch for this
  // form's copy of the fields that save changed.
  const [loadedAt, setLoadedAt] = useState<string | null>(setup.updated_at)
  // The API's answer to the last save. Any edit retires it: the next save
  // is a new question.
  const [saveError, setSaveError] = useState<string | null>(null)
  const save = useSaveEvalsConfig(teamId, creates, {
    onSaved: setLoadedAt,
    onRefused: setSaveError,
  })
  const dirty = isFormDirty(saved, form)
  const validationError = dirty ? validateForm(form, adapters) : null
  const canSubmit = !save.isPending && dirty && validationError === null

  const edit = (next: (previous: EvalsConfigForm) => EvalsConfigForm): void => {
    setForm(next)
    setSaveError(null)
  }
  return {
    form,
    update: (key, value) => {
      edit((previous) => ({ ...previous, [key]: value }))
    },
    edit,
    creates,
    dirty,
    pending: save.isPending,
    canSubmit,
    error: validationError ?? saveError,
    discard: () => {
      setForm(saved)
      setLoadedAt(setup.updated_at)
      setSaveError(null)
    },
    submit: (e) => {
      e.preventDefault()
      if (!canSubmit) return
      setSaveError(null)
      save.mutate({ teamId, data: { ...toBody(form), expected_updated_at: loadedAt } })
    },
  }
}

interface SaveCallbacks {
  /** The version the save wrote, which the form now stands on. */
  onSaved: (updatedAt: string | null) => void
  /** A refusal the operator can act on from the form. */
  onRefused: (detail: string) => void
}

/**
 * The PUT, with where each answer goes: a refusal the operator can act
 * on from the form goes to `onRefused`, anything else to a toast.
 */
function useSaveEvalsConfig(
  teamId: string,
  creates: boolean,
  { onSaved, onRefused }: SaveCallbacks,
): ReturnType<typeof useUpdateTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigPut> {
  const queryClient = useQueryClient()
  return useUpdateTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigPut({
    mutation: {
      onSuccess: (result) => {
        if (result.status === 200) {
          onSaved(result.data.updated_at)
          queryClient.setQueryData(
            getGetTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGetQueryKey(teamId),
            result,
          )
        }
        // The installation status checks the scored repository, so it is
        // read again once the benchmarks may have changed.
        void queryClient.invalidateQueries({
          queryKey: getGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGetQueryKey(teamId),
        })
        toast.success(creates ? 'Evals config created' : 'Evals config saved')
      },
      onError: (error: unknown) => {
        const detail = getApiErrorDetail(error) ?? 'Failed to save the evals config'
        const status = getApiErrorStatus(error)
        // What the operator can act on from the form, 409 included: the
        // row moved after this form loaded (another tab, page or staff
        // member saved), and the answer says to reload. The PUT upserts,
        // so it has no 404 to answer; a 502/503 is the stack and goes to
        // a toast.
        if (status === 400 || status === 409 || status === 422) onRefused(detail)
        else toast.error(detail)
      },
    },
  })
}
